import axios, {
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { setupCache } from "axios-cache-interceptor";
import { BACKEND_API_URL } from "./constants";
import {
  HttpHeader,
  HttpHeaderKey,
  HttpHeaderValue,
  HttpStatusCode,
} from "./apiStatus.enum";
import { getAccessTokenFromLocal } from "./common";
import { navigateTo } from "./navigation";

const instance = axios.create({
  baseURL: BACKEND_API_URL,
  headers: {
    [HttpHeader.CONTENT_TYPE]: HttpHeaderValue.ApplicationJson,
  },
});

const httpClient = setupCache(instance);

let loginInProgress = false;
let loginPromise: Promise<void> | null = null;

export function setLoginInProgress(inProgress: boolean) {
  loginInProgress = inProgress;
  if (!inProgress) {
    loginPromise = null;
  }
}

export function setLoginPromise(promise: Promise<void>) {
  loginPromise = promise;
}

// Add a request interceptor
httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const isLoginCall = config.url === "/login";

    if (!isLoginCall && loginInProgress && loginPromise) {
      await loginPromise;
    }

    if (!config.headers) (config.headers as AxiosHeaders).set({});
    const ACCESS_TOKEN = getAccessTokenFromLocal();
    config.headers[HttpHeaderKey.Authorization] = "Bearer " + ACCESS_TOKEN;

    // Only set Content-Type to application/json if not already set and data is not FormData
    if (
      !config.headers[HttpHeader.CONTENT_TYPE] &&
      !(config.data instanceof FormData)
    ) {
      config.headers[HttpHeader.CONTENT_TYPE] = HttpHeaderValue.ApplicationJson;
    }

    return config;
  },
  (error) => {
    // Do something with request error
    console.error("Error in request interceptor:", error);
    return Promise.reject(error);
  },
);

httpClient.interceptors.response.use(
  (res: AxiosResponse<unknown>) => {
    return res;
  },
  (err) => {
    if (
      !loginInProgress &&
      err?.response?.status === HttpStatusCode.Unauthorized &&
      (err?.response?.data?.Message === "Unauthorized" ||
        err?.response?.data?.Message === "User is not authorized" ||
        err?.response?.data?.Message === "Token mismatch")
    ) {
      const parsedUrl = new URL(window.location.href);
      // Access the pathname property
      const path = parsedUrl.pathname || "/";
      window.sessionStorage.setItem("redirectTo", path);
      window.localStorage.setItem("isTokenMismatch", "true");
      window.dispatchEvent(new Event("resetTokenMismatch"));
    } else if (
      err?.response?.status === HttpStatusCode.Forbidden &&
      err?.response?.data?.Message ===
        "You do not have permission to access this request."
    ) {
      navigateTo("/error", {
        replace: true,
        state: {
          status: HttpStatusCode.Forbidden,
        },
      });
    }
    return Promise.reject(err);
  },
);

export default httpClient;
